"use client";

import { Box } from "@chakra-ui/react";
import { AnimatePresence, motion } from "framer-motion";
import { type ReactNode, useEffect } from "react";
import { Button } from "@/components";
import { useDashboardSidebar } from "@/lib/contexts";
import { useThemeTokens } from "@/lib/theme/tokens";

export interface AdminSelectionStickyBarProps {
  isVisible: boolean;
  selectedCount: number;
  onUnselectAll: () => void;
  isUnselectLoading?: boolean;
  leading?: ReactNode;
  children: ReactNode;
  testId?: string;
}

export function AdminSelectionStickyBar({
  isVisible,
  selectedCount,
  onUnselectAll,
  isUnselectLoading = false,
  leading,
  children,
  testId = "admin-selection-sticky-bar",
}: AdminSelectionStickyBarProps) {
  const theme = useThemeTokens();
  const { sidebarOffset } = useDashboardSidebar();

  useEffect(() => {
    if (!isVisible) {
      delete document.documentElement.dataset.stickyActionBar;
      return;
    }

    document.documentElement.dataset.stickyActionBar = "true";
    return () => {
      delete document.documentElement.dataset.stickyActionBar;
    };
  }, [isVisible]);

  return (
    <AnimatePresence>
      {isVisible && (
        <motion.div
          key={testId}
          data-testid={testId}
          initial={{ opacity: 0, y: "100%", left: sidebarOffset }}
          animate={{ opacity: 1, y: 0, left: sidebarOffset }}
          exit={{ opacity: 0, y: "100%" }}
          transition={{
            opacity: { duration: 0.32, ease: [0.32, 0.72, 0, 1] },
            y: { duration: 0.32, ease: [0.32, 0.72, 0, 1] },
            left: { duration: 0.2, ease: "easeInOut" },
          }}
          style={{
            position: "fixed",
            bottom: 0,
            right: 0,
            zIndex: 1100,
          }}
        >
          <Box
            px={{ base: 4, lg: 8 }}
            pt={{ base: 3, md: 4 }}
            pb={{
              base: "calc(12px + env(safe-area-inset-bottom, 0px))",
              md: 4,
            }}
            bg={theme.bg.primary}
            borderTopWidth="1px"
            borderColor={theme.borderGlass.default}
            boxShadow="0 -8px 24px rgba(0, 0, 0, 0.12)"
          >
            <Box
              display="grid"
              w="100%"
              gap={3}
              alignItems="center"
              justifyContent={{
                base: "stretch",
                md: leading ? "space-between" : "end",
              }}
              gridTemplateColumns={{
                base: leading
                  ? "minmax(0, 1fr) auto"
                  : "minmax(0, 1fr) minmax(0, 1fr)",
                md: leading ? "minmax(0, 1fr) auto auto" : "auto auto",
              }}
            >
              {leading ? (
                <Box
                  minW={0}
                  overflow="hidden"
                  sx={{
                    "& > *": {
                      maxW: "100%",
                      overflow: "hidden",
                    },
                  }}
                >
                  {leading}
                </Box>
              ) : null}
              <Button
                variant="outline"
                h="44px"
                w={{ base: leading ? "auto" : "100%", md: "auto" }}
                flexShrink={0}
                whiteSpace="nowrap"
                onClick={onUnselectAll}
                isLoading={isUnselectLoading}
                loadingText="Loading..."
                isDisabled={isUnselectLoading}
                data-testid={`${testId}-unselect-all`}
              >
                {leading ? (
                  <>
                    <Box as="span" display={{ md: "none" }}>
                      Unselect
                    </Box>
                    <Box as="span" display={{ base: "none", md: "inline" }}>
                      Unselect all ({selectedCount})
                    </Box>
                  </>
                ) : (
                  `Unselect all (${selectedCount})`
                )}
              </Button>
              <Box
                minW={0}
                w={{ base: "100%", md: "auto" }}
                gridColumn={{ base: leading ? "1 / -1" : "auto", md: "auto" }}
                flexShrink={0}
              >
                {children}
              </Box>
            </Box>
          </Box>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
